import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  increment,
  writeBatch,
  limit,
  orderBy,
  startAfter,
  DocumentSnapshot,
  QueryConstraint
} from 'firebase/firestore';
import { db } from '../config';

// Types
export interface Contact {
  id: string;
  email: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  customFields?: Record<string, any>;
  listIds?: string[];
  tags?: string[];
  preferences?: {
    emailEnabled: boolean;
    smsEnabled?: boolean;
    topicPreferences?: Record<string, boolean>;
  };
  unsubscribedAt?: number;
  unsubscribeToken?: string;
  createdAt: number;
  updatedAt: number;
  transactions?: number;
}

// Collection references
const contactsRef = collection(db, 'contacts');

// Contact CRUD operations
export const getContacts = async (
  pageSize: number = 20, 
  lastVisible?: DocumentSnapshot,
  filters?: { field: string; operator: string; value: any }[]
): Promise<{ contacts: Contact[]; lastVisible: DocumentSnapshot | null }> => {
  try {
    const constraints: QueryConstraint[] = [orderBy('createdAt', 'desc'), limit(pageSize)];
    
    // Apply filters if provided
    if (filters && filters.length > 0) {
      filters.forEach(filter => {
        constraints.push(where(filter.field, filter.operator as any, filter.value));
      });
    }
    
    // Apply pagination if lastVisible is provided
    if (lastVisible) {
      constraints.push(startAfter(lastVisible));
    }
    
    const q = query(contactsRef, ...constraints);
    const snapshot = await getDocs(q);
    
    const contacts = snapshot.docs.map(doc => (({
      ...doc.data(),
      id: doc.id
    }) as Contact));
    const newLastVisible = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
    
    return { contacts, lastVisible: newLastVisible };
  } catch (error) {
    console.error('Error getting contacts:', error);
    throw error;
  }
};

// Delete a contact
export const deleteContact = async (id: string): Promise<void> => {
  try {
    await deleteDoc(doc(contactsRef, id));
  } catch (error) {
    console.error('Error deleting contact:', error);
    throw error;
  }
};

// Get contacts in a list
export const getContactsInList = async (listId: string): Promise<Contact[]> => {
  try {
    const q = query(contactsRef, where('listIds', 'array-contains', listId));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => (({
      ...doc.data(),
      id: doc.id
    }) as Contact));
  } catch (error) {
    console.error('Error getting contacts in list:', error);
    throw error;
  }
};

// Create a new contact
export const createContact = async (
  contactData: Omit<Contact, 'id' | 'createdAt' | 'updatedAt'>
): Promise<Contact> => {
  try {
    const contactId = crypto.randomUUID();
    const timestamp = Date.now();
    
    const contact: Contact = {
      id: contactId,
      ...contactData,
      createdAt: timestamp,
      updatedAt: timestamp,
      preferences: {
        emailEnabled: true,
        ...contactData.preferences
      }
    };
    
    await setDoc(doc(contactsRef, contactId), contact);
    return contact;
  } catch (error) {
    console.error('Error creating contact:', error);
    throw error;
  }
};

// Get contact by unsubscribe token
export const getContactByUnsubscribeToken = async (token: string): Promise<Contact | null> => {
  try {
    const q = query(contactsRef, where('unsubscribeToken', '==', token), limit(1));
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return null;
    }
    
    const doc = snapshot.docs[0];
    return {
      ...doc.data(),
      id: doc.id
    } as Contact;
  } catch (error) {
    console.error('Error getting contact by unsubscribe token:', error);
    throw error;
  }
};

// Update a contact
export const updateContact = async (
  id: string, 
  contactData: Partial<Omit<Contact, 'id' | 'createdAt'>>
): Promise<void> => {
  try {
    const timestamp = Date.now();
    
    await updateDoc(doc(contactsRef, id), {
      ...contactData,
      updatedAt: timestamp
    });
  } catch (error) {
    console.error('Error updating contact:', error);
    throw error;
  }
}; 